import React, { Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "react-hot-toast";

// New design system
import { AppThemeProvider } from "./design/ThemeContext";

// Auth context
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { TradingProvider } from "./contexts/TradingContext";

// Layout
import Sidebar from "./components/layout/Sidebar";
import Navbar from "./components/layout/Navbar";
import { LoadingScreen } from "./components/ui";
import { useAppTheme } from "./design/ThemeContext";

// Routes
import routes from "./routes";

// Auth views - Lazy loaded
const Login = lazy(() => import("./components/auth/Login"));
const Signup = lazy(() => import("./components/auth/Signup"));
const OAuthCallback = lazy(() => import("./components/auth/OAuthCallback"));
const VerifyEmail = lazy(() => import("./components/auth/VerifyEmail"));
const LandingPage = lazy(() => import("./components/landing/LandingPage"));

function AppContent() {
  const { pathname } = useLocation();
  const { isAuthenticated, user, loading } = useAuth();
  const { sidebarCollapsed } = useAppTheme();

  // Filter routes based on user permissions
  const filteredRoutes = routes.filter((route) => {
    if (route.adminOnly && (!user || !user.is_admin)) {
      return false;
    }
    return true;
  });

  const getRoutes = (allRoutes) =>
    allRoutes
      .map((route) => {
        const routeElements = [];
        if (route.route) {
          const Component = route.component;
          const element = <Component />;
          routeElements.push(
            <Route exact path={route.route} element={element} key={route.key} />
          );
        }
        if (route.collapse) {
          routeElements.push(...getRoutes(route.collapse));
        }
        return routeElements;
      })
      .flat();

  // Show loading screen
  if (loading) {
    return <LoadingScreen fullScreen message="Loading your dashboard..." />;
  }

  // Unauthenticated - show auth pages
  if (!isAuthenticated) {
    return (
      <Suspense fallback={<LoadingScreen fullScreen />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Signup />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/oauth/callback" element={<OAuthCallback />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    );
  }

  // Authenticated - show dashboard
  return (
    <TradingProvider>
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
      {/* Sidebar */}
      <Sidebar routes={filteredRoutes} />

      {/* Main content area */}
      <div
        className="flex flex-col min-h-screen transition-all duration-300"
        style={{
          marginLeft: sidebarCollapsed ? 0 : 'var(--sidebar-width)',
        }}
      >
        {/* Navbar */}
        <Navbar />

        {/* Page content */}
        <main
          className="flex-1 p-5 md:p-8"
          style={{ backgroundColor: 'var(--bg-primary)' }}
        >
          <Suspense fallback={<LoadingScreen message="Loading page..." />}>
            <Routes>
              {getRoutes(filteredRoutes)}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Suspense>
        </main>

        {/* Footer */}
        <footer
          className="flex items-center justify-between px-6 py-4 border-t"
          style={{ borderColor: 'var(--border-primary)' }}
        >
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
            &copy; {new Date().getFullYear()} TraderMind AI. All rights reserved.
          </span>
          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
            v1.0.0
          </span>
        </footer>
      </div>

      {/* Toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: 'var(--bg-card)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-primary)',
            borderRadius: '12px',
            fontFamily: '"Plus Jakarta Sans", sans-serif',
            fontSize: '0.875rem',
          },
          success: {
            iconTheme: { primary: 'var(--color-success)', secondary: '#fff' },
          },
          error: {
            iconTheme: { primary: 'var(--color-error)', secondary: '#fff' },
          },
        }}
      />
    </div>
    </TradingProvider>
  );
}

function App() {
  return (
    <AppThemeProvider>
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
    </AppThemeProvider>
  );
}

export default App;
